import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Builds a compact list of page numbers with ellipses, e.g. [1, '...', 4, 5, 6, '...', 12].
 */
function getPageItems(current, totalPages) {
  const delta = 1;
  const range = [];
  const rangeWithDots = [];
  let last;

  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= current - delta && i <= current + delta)) {
      range.push(i);
    }
  }

  for (const i of range) {
    if (last) {
      if (i - last === 2) {
        rangeWithDots.push(last + 1);
      } else if (i - last !== 1) {
        rangeWithDots.push("...");
      }
    }
    rangeWithDots.push(i);
    last = i;
  }

  return rangeWithDots;
}

function Pagination({ page, pageSize, total, onPageChange }) {
  const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize));

  if (totalPages <= 1) return null;

  const items = getPageItems(page, totalPages);
  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, total);

  const buttonBase =
    "min-w-[40px] h-10 px-3 rounded-xl text-sm font-bold flex items-center justify-center transition-all";

  return (
    <nav
      className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4"
      aria-label="Pagination"
    >
      <p className="text-sm text-gray-500 font-medium order-2 sm:order-1">
        Showing <span className="font-bold text-gray-700">{startItem}</span>–
        <span className="font-bold text-gray-700">{endItem}</span> of{" "}
        <span className="font-bold text-gray-700">{total}</span> retreats
      </p>

      <div className="flex items-center gap-1.5 order-1 sm:order-2">
        <button
          type="button"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          className={`${buttonBase} bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed`}
          aria-label="Previous page"
        >
          <ChevronLeft size={16} />
        </button>

        {items.map((item, idx) =>
          item === "..." ? (
            <span key={`dots-${idx}`} className="min-w-[40px] h-10 flex items-center justify-center text-gray-400">
              …
            </span>
          ) : (
            <button
              key={item}
              type="button"
              onClick={() => onPageChange(item)}
              aria-current={item === page ? "page" : undefined}
              className={`${buttonBase} ${
                item === page
                  ? "bg-emerald-600 text-white shadow-md shadow-emerald-200"
                  : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
              }`}
            >
              {item}
            </button>
          )
        )}

        <button
          type="button"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          className={`${buttonBase} bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed`}
          aria-label="Next page"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </nav>
  );
}

export default Pagination;
